import React, { useCallback, useContext, useState, useEffect } from 'react';

import KeyboardArrowLeftRoundedIcon from '@material-ui/icons/KeyboardArrowLeftRounded';
import { navigate } from '@reach/router';
import { Paper } from '@material-ui/core';

import { GlobalContext } from '../../../../store';
import { IEventEnrollment, IEventItem } from '../../../../store/player/events/store';
import { IGame } from '../../../../store/player/games/store';
import { IScore } from '../../../../store/player/scores/store';

import { StyledSetCTPWinner } from './SetCTPWinner.Styles';
import { Button } from '../../../../components/Button/Button';
import { userPic } from '../../../../assets/images';
import Input from '../../../../components/Input';
import { feetAndInchesToInches } from '../../../../contrib/Utils/feetAndInchesToInches';
import { inchesToFeetAndInches } from '../../../../contrib/Utils/inchesToFeetAndInches';
import { goToNextHole } from '../SetHoleScores/Components/goToNextHole';
import { useShotgunGameMetadata } from '../../ClubHome/CurrentEvent/Components/CurrentEventTeeTimeParings/useShotgunGameMetadata';
import useGetHoleRange from '../Utils/getHoleRange';

interface IProps {
  roundId: number;
  eventId: number;
  holeIndex: number;
  enrollmentId: string;
}

const SetCTPWinner = (props: IProps) => {
  const { eventId, roundId, holeIndex, enrollmentId } = props;

  const {
    actions: {
      scores: { get: getScores, save: saveScores },
      notifications: { pushError, pushSuccess }
    },
    games,
    events,
    scores
  } = useContext(GlobalContext);

  const [game, setGame] = useState<IGame>();
  const [holeScoreId, setHoleScoreId] = useState<number>();
  const [event, setEvent] = useState<IEventItem>();
  const [scorer, setScorer] = useState<IEventEnrollment>();
  const [feet, setFeet] = useState<number>();
  const [inches, setInches] = useState<number>();

  const holesRange = useGetHoleRange(eventId, roundId);
  const { isShotgun, startingHole } = useShotgunGameMetadata(game);

  useEffect(() => {
    setGame(Object.values(games).find(g => g.round.id === Number(roundId)));
  }, [games, roundId]);

  useEffect(() => {
    if (game) {
      getScores(game.group.id);
    }
  }, [game]);

  useEffect(() => {
    if (scores && game) {
      const groupId = game.group.id;

      if (scores[groupId]) {
        const score = scores[groupId].find(sScore => sScore.enrollment === Number(enrollmentId));

        if (score) {
          const holeScore = score.scores.find(hScore => hScore.order === holeIndex - 1);

          if (holeScore) {
            const processedDistance = inchesToFeetAndInches(holeScore.pinDistance);

            if (processedDistance) {
              setFeet(processedDistance.feet);
              setInches(processedDistance.inches);
            }
            setHoleScoreId(holeScore.id);
          }
        }
      }
    }
  }, [scores, game, enrollmentId, holeIndex]);

  useEffect(() => {
    if (events[eventId]) {
      setEvent(events[eventId]);
    }
  }, [events, eventId]);

  useEffect(() => {
    if (event) {
      setScorer(event.enrollments.find(enrollment => enrollment.id === Number(enrollmentId)));
    }
  }, [event, enrollmentId]);

  const setValue = (setter: typeof setFeet | typeof setInches) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;

    if (!value.includes('-')) {
      setter(!!value ? Number(value) : undefined);
    }
  };

  const goToSelectWinner = useCallback(
    () => navigate(`/event-scores/${eventId}/round/${roundId}/hole/${holeIndex}/selectctpwinner`),
    [eventId, roundId, holeIndex]
  );

  const onGoToNextHole = useCallback(
    () =>
      goToNextHole({
        eventId,
        holeIndex,
        holesRange,
        isShotgun,
        roundId,
        startingHole
      }),
    [eventId, roundId, isShotgun, holeIndex, holesRange, startingHole]
  );

  const submit = useCallback(async () => {
    if (game) {
      const hole = game.currentTee && game.currentTee.holes.find(h => h.order === holeIndex - 1);
      const par = hole ? hole.par : 0;

      const groupId = game.group.id;

      const gameScore =
        Object.keys(scores).length > 0 && scores[game.group.id]
          ? scores[game.group.id].find((g: IScore) => g.enrollment === Number(enrollmentId))
          : undefined;

      const score = holeScoreId
        ? {
            [holeScoreId]: {
              id: holeScoreId,
              pin_distance: feetAndInchesToInches({ feet, inches })
            }
          }
        : gameScore
        ? {
            [enrollmentId]: {
              empty: true,
              game: gameScore,
              net_score: par,
              order: holeIndex - 1,
              original: par,
              pin_distance: feetAndInchesToInches({ feet, inches })
            }
          }
        : {};

      try {
        await saveScores(groupId, score);

        pushSuccess('CTP saved successfully');

        onGoToNextHole();
      } catch (error) {
        pushError('CTP could not be saved, please try again');
      }
    }
  }, [game, holeIndex, holeScoreId, feet, inches, enrollmentId, scores]);

  return (
    <StyledSetCTPWinner className={`score-route set-hole-scores view-scores`}>
      <div className="title">
        <Button
          classes={{
            label: 'label',
            root: 'back-to-scores-button'
          }}
          onClick={goToSelectWinner}
        >
          <KeyboardArrowLeftRoundedIcon classes={{ root: 'icon' }} />
          BACK
        </Button>

        <h2>Winner</h2>
      </div>

      {scorer && (
        <div className="scorer">
          <img
            src={scorer.user.profile.image ? scorer.user.profile.image : userPic}
            alt={`${scorer.user.firstName} ${scorer.user.lastName}`}
          />
          <div className="scorer-name">
            {scorer.user.firstName} {scorer.user.lastName}
          </div>
        </div>
      )}

      <Paper className="paper">
        <h2>ENTER DISTANCE</h2>

        <div className="inputs">
          <div className="input-container">
            <Input
              type="number"
              inputProps={{ min: 0 }}
              value={feet}
              onChange={setValue(setFeet)}
            />
            <label>FEET</label>
          </div>

          <div className="input-container">
            <Input
              type="number"
              inputProps={{ min: 0 }}
              value={inches}
              onChange={setValue(setInches)}
            />
            <label>INCHES</label>
          </div>
        </div>
      </Paper>

      <Button
        classes={{
          label: 'label',
          root: 'submit'
        }}
        onClick={submit}
        color="primary"
        variant="contained"
        disabled={!feet && !inches}
      >
        SUBMIT
      </Button>
    </StyledSetCTPWinner>
  );
};

export default SetCTPWinner;
